import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { FileDto, TicketDto, TicketMessageDto, TicketPriority, TicketStatus } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { readUpload, saveBlob, sizeLabel, type UploadDraft } from '../files';
import { fmtDateTime } from '../format';
import { Empty, ErrorBanner, Field, Modal, Panel } from '../ui';

const STATUS: Record<TicketStatus, string> = { open: 'Aperta', in_progress: 'In lavorazione', resolved: 'Risolta' };
const PRIORITY: Record<TicketPriority, string> = { low: 'Bassa', normal: 'Normale', high: 'Alta' };

/** Richieste di assistenza: il cliente le apre (con allegati), il consulente risponde e le chiude. */
export function TicketsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const isClient = ctx.me.user.role === 'client';
  const [tickets, setTickets] = useState<TicketDto[] | null>(null);
  const [filter, setFilter] = useState<'active' | 'resolved'>('active');
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTickets(await api.get<TicketDto[]>('/api/tickets'));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (ctx.event?.type === 'ticket.updated') void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.eventSeq]);

  const visible = (tickets ?? []).filter((t) => (filter === 'resolved' ? t.status === 'resolved' : t.status !== 'resolved'));
  const openCount = (tickets ?? []).filter((t) => t.status !== 'resolved').length;

  return (
    <div className="panels single">
      <Panel
        title="Assistenza"
        subtitle={
          tickets
            ? `${isClient ? 'Chiedi aiuto al tuo consulente' : 'Le richieste di aiuto dei clienti'}: ${openCount} ${openCount === 1 ? 'aperta' : 'aperte'}`
            : undefined
        }
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            <select value={filter} title="Quali richieste vedere" onChange={(e) => setFilter(e.target.value as 'active' | 'resolved')}>
              <option value="active">Aperte</option>
              <option value="resolved">Risolte</option>
            </select>
            <button type="button" className="btn dark" title={isClient ? 'Chiedi aiuto: ti rispondiamo qui' : 'Apri una richiesta a nome di un cliente'} onClick={() => setCreating(true)}>
              <Icon name="plus" size={16} /> Nuova richiesta
            </button>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {!tickets ? (
          <div className="muted small">Caricamento…</div>
        ) : visible.length === 0 ? (
          <Empty icon="chat">{filter === 'active' ? 'Nessuna richiesta aperta.' : 'Nessuna richiesta risolta.'}</Empty>
        ) : (
          <div className="ag-list">
            {visible.map((t) => (
              <div key={t.id} className="ag-row" onClick={() => setOpenId(t.id)}>
                <span className={`tk-prio ${t.priority}`} title={`Priorità ${PRIORITY[t.priority].toLowerCase()}`} />
                <span className="ag-title">
                  <b>{t.subject}</b>
                  <div className="muted small">
                    {!isClient && `${t.clientName} · `}
                    {STATUS[t.status]} · {t.messagesCount} {t.messagesCount === 1 ? 'messaggio' : 'messaggi'} · {fmtDateTime(t.updatedAt)}
                  </div>
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>
      {creating && (
        <NewTicket
          ctx={ctx}
          onClose={() => setCreating(false)}
          onCreated={(t) => {
            setCreating(false);
            setOpenId(t.id);
            void load();
          }}
        />
      )}
      {openId && (
        <Thread
          ctx={ctx}
          id={openId}
          onClose={() => {
            setOpenId(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function AttachPicker({ files, onChange, onError }: { files: UploadDraft[]; onChange: (f: UploadDraft[]) => void; onError: (m: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const pick = async (list: FileList | null) => {
    if (!list) return;
    try {
      const read = await Promise.all([...list].map(readUpload));
      onChange([...files, ...read].slice(0, 5));
    } catch (e) {
      onError((e as Error).message);
    }
    if (ref.current) ref.current.value = '';
  };
  return (
    <div>
      <input ref={ref} type="file" multiple hidden onChange={(e) => void pick(e.target.files)} />
      <button type="button" className="btn small" onClick={() => ref.current?.click()} disabled={files.length >= 5}>
        <Icon name="paperclip" size={14} /> Allega file
      </button>
      {files.length > 0 && (
        <ul className="tk-files">
          {files.map((f, i) => (
            <li key={i}>
              {f.name} <span className="muted">({sizeLabel(f.size)})</span>{' '}
              <button type="button" className="btn ghost" onClick={() => onChange(files.filter((_, k) => k !== i))} title="Togli">
                <Icon name="x" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="muted small">Fino a 5 file, 10 MB ciascuno.</div>
    </div>
  );
}

function NewTicket({ ctx, onClose, onCreated }: { ctx: Ctx; onClose: () => void; onCreated: (t: TicketDto) => void }) {
  const { api } = ctx;
  const isClient = ctx.me.user.role === 'client';
  const [clients, setClients] = useState<{ id: number; name: string }[]>([]);
  const [clientId, setClientId] = useState<number | ''>('');
  const [subject, setSubject] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<UploadDraft[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isClient) api.get<{ id: number; name: string }[]>('/api/quotes/meta/clients').then(setClients).catch(() => undefined);
  }, [api, isClient]);

  const send = async () => {
    if (!isClient && !clientId) return setError('Scegli il cliente');
    if (!subject.trim() || !body.trim()) return setError('Indica oggetto e descrizione');
    setBusy(true);
    try {
      onCreated(
        await api.post<TicketDto>('/api/tickets', {
          clientId: isClient ? undefined : clientId,
          subject: subject.trim(),
          priority,
          body: body.trim(),
          files: files.map(({ name, mime, contentBase64 }) => ({ name, mime, contentBase64 })),
        }),
      );
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Nuova richiesta di assistenza"
      onClose={onClose}
      footer={
        <div className="quote-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void send()} disabled={busy}>
            {busy ? 'Invio…' : 'Invia'}
          </button>
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {!isClient && (
        <Field label="Cliente">
          <select value={clientId} onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">— Scegli —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Oggetto">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} autoFocus placeholder="Es. Il sito non carica le immagini" />
      </Field>
      <Field label="Priorità">
        <select value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
          {(Object.keys(PRIORITY) as TicketPriority[]).map((p) => (
            <option key={p} value={p}>
              {PRIORITY[p]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Descrizione">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Cosa succede? Da quando? Cosa ti aspettavi?" />
      </Field>
      <AttachPicker files={files} onChange={setFiles} onError={setError} />
    </Modal>
  );
}

function Thread({ ctx, id, onClose }: { ctx: Ctx; id: number; onClose: () => void }) {
  const { api } = ctx;
  const isClient = ctx.me.user.role === 'client';
  const [data, setData] = useState<{ ticket: TicketDto; messages: TicketMessageDto[] } | null>(null);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<UploadDraft[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get(`/api/tickets/${id}`));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, id]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (ctx.event?.type === 'ticket.updated' && ctx.event.ticket.id === id) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.eventSeq]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/api/tickets/${id}/reply`, { body: body.trim(), files: files.map(({ name, mime, contentBase64 }) => ({ name, mime, contentBase64 })) });
      setBody('');
      setFiles([]);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: TicketStatus) => {
    try {
      await api.post(`/api/tickets/${id}/status`, { status });
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const download = async (f: FileDto) => {
    try {
      saveBlob(await api.download(`/api/files/${f.id}/download`), f.name);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const t = data?.ticket;
  return (
    <Modal
      title={t ? t.subject : 'Richiesta'}
      onClose={onClose}
      footer={
        t && (
          <div className="quote-actions">
            {t.status === 'resolved' ? (
              <button type="button" className="btn" title="Il problema c’è ancora: la richiesta torna aperta" onClick={() => void setStatus('open')}>
                Riapri
              </button>
            ) : (
              <button type="button" className="btn" title="Chiude la richiesta (si può riaprire)" onClick={() => void setStatus('resolved')}>
                <Icon name="check" size={15} /> Segna come risolta
              </button>
            )}
            <button type="button" className="btn dark" onClick={() => void send()} disabled={busy || (!body.trim() && files.length === 0)}>
              {busy ? 'Invio…' : 'Rispondi'}
            </button>
          </div>
        )
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {!data || !t ? (
        <div className="muted small">Caricamento…</div>
      ) : (
        <>
          <div className="muted small" style={{ marginBottom: 10 }}>
            {!isClient && `${t.clientName} · `}
            {STATUS[t.status]} · priorità {PRIORITY[t.priority].toLowerCase()}
            {!isClient && t.status !== 'resolved' && (
              <>
                {' · '}
                <select value={t.status} title="Cambia lo stato: il cliente lo vede" onChange={(e) => void setStatus(e.target.value as TicketStatus)}>
                  {(Object.keys(STATUS) as TicketStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {STATUS[s]}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
          <div className="tk-thread">
            {data.messages.map((m) => (
              <div key={m.id} className={`tk-msg${m.role === 'client' ? '' : ' staff'}`}>
                <div className="muted small">
                  <b>{m.userName}</b> · {fmtDateTime(m.createdAt)}
                </div>
                <div className="tk-body">{m.body}</div>
                {m.files.map((f) => (
                  <button key={f.id} type="button" className="btn small" onClick={() => void download(f)}>
                    <Icon name="download" size={13} /> {f.name} <span className="muted">({sizeLabel(f.size)})</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
          <Field label="Rispondi">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} />
          </Field>
          <AttachPicker files={files} onChange={setFiles} onError={setError} />
        </>
      )}
    </Modal>
  );
}
